import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../../api/client';
import { queryKeys } from './queryKeys';
import useProtectedQueryEnabled from './useProtectedQueryEnabled';

export async function fetchProfile(signal) {
  const res = await api.get('/user/profile', { signal });
  return res.data;
}

export async function fetchReferral(signal) {
  const res = await api.get('/referral/info', { signal });
  return res.data;
}

export async function fetchSubscriptionCurrent(signal) {
  const res = await api.get('/subscription/current', { signal });
  return res.data;
}

export async function fetchSubscriptionPlans(signal) {
  const res = await api.get('/subscription/plans', { signal });
  return res.data;
}

export async function fetchCreditsPackages(signal) {
  const res = await api.get('/credits/packages', { signal });
  return res.data;
}

export function useProfileQuery(options = {}) {
  const { enabled = true, ...rest } = options;
  const protectedEnabled = useProtectedQueryEnabled(enabled);
  return useQuery({
    queryKey: queryKeys.profile(),
    queryFn: ({ signal }) => fetchProfile(signal),
    ...rest,
    enabled: protectedEnabled,
  });
}

export function useUpdateProfileLanguageMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (language) =>
      api.patch('/user/profile', { language }).then((res) => res.data),
    onSuccess: ({ user }) => {
      queryClient.setQueryData(queryKeys.profile(), (current) => {
        if (!current) return current;
        if (current.user) {
          return { ...current, user: { ...current.user, language: user.language } };
        }
        return { ...current, language: user.language };
      });
    },
  });
}

export function useReferralQuery(options = {}) {
  const { enabled = true, ...rest } = options;
  const protectedEnabled = useProtectedQueryEnabled(enabled);
  return useQuery({
    queryKey: queryKeys.referral(),
    queryFn: ({ signal }) => fetchReferral(signal),
    ...rest,
    enabled: protectedEnabled,
  });
}

export function useSubscriptionCurrentQuery(options = {}) {
  const { enabled = true, ...rest } = options;
  const protectedEnabled = useProtectedQueryEnabled(enabled);
  return useQuery({
    queryKey: queryKeys.subscriptionCurrent(),
    queryFn: ({ signal }) => fetchSubscriptionCurrent(signal),
    ...rest,
    enabled: protectedEnabled,
  });
}

export function useSubscriptionPlansQuery(options = {}) {
  return useQuery({
    queryKey: queryKeys.subscriptionPlans(),
    queryFn: ({ signal }) => fetchSubscriptionPlans(signal),
    ...options,
  });
}

export function useCreditsPackagesQuery(options = {}) {
  return useQuery({
    queryKey: queryKeys.creditsPackages(),
    queryFn: ({ signal }) => fetchCreditsPackages(signal),
    ...options,
  });
}
